import { FileIcon, PlusIcon } from '@radix-ui/react-icons';
import { Badge, Card, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { CopyPitchesDialog } from 'components/common/dialogs/copy-pitches';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonContentWithSidebar } from 'components/common/layout/content-with-sidebar';
import { SectionHeader } from 'components/sections/header';
import { BallFlightDesigner } from 'components/sections/pitch-design/ball-flight-designer';
import { InputModeForm } from 'components/sections/pitch-design/input-mode';
import { SeamOrientation } from 'components/sections/pitch-design/seam-orientation';
import { PDSidebar } from 'components/sections/pitch-design/sidebar';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { cloneDeep, first } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NavLocationState } from 'routes/nav-state';
import { paths } from 'routes/paths';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import useListLibraryStore from 'stores/use-list-library-store';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';
import { v4 } from 'uuid';

const COMPONENT_NAME = 'PitchDesign';

// for beta to work with prod server, this must be false and we must send users back to the parent list on update
const ENABLE_REPEATED_UPDATES = false;

interface IProps {
  asDialog?: boolean;
  onPitchUpdated?: (pitch: IPitch) => void;
}

export const PitchDesign = (props: IProps & IVerboseComponent) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listId } = useParams();
  const fromListId = (location.state as NavLocationState | null)?.fromListId;

  const { machine } = useContext(MachineContext);

  const inputs = usePitchDesignStore((s) => s.inputs);
  const isNew = usePitchDesignStore((s) => s.isNew);
  const priority = usePitchDesignStore((s) => s.priority);
  const refPitch = usePitchDesignStore((s) => s.refPitch);

  const derivedTraj = usePitchDesignStore((s) => s.derivedTraj);
  const derivedBs = usePitchDesignStore((s) => s.derivedBs);

  const activeList = useListLibraryStore((s) => s.activeList);

  const [loading, setLoading] = useState(false);

  const [dialogSave, setDialogSave] = useState<{
    key: number;
    pitch: Partial<IPitch>;
  }>();

  const pitchPayload = useMemo((): Partial<IPitch> => {
    const output: Partial<IPitch> = cloneDeep(refPitch);

    if (!output._id) {
      output._id = `temp-${v4()}`;
    }

    if (inputs.videoIDs.stretchVideoID) {
      output.stretchVideoID = inputs.videoIDs.stretchVideoID;
    }

    if (inputs.videoIDs.windupVideoID) {
      output.windupVideoID = inputs.videoIDs.windupVideoID;
    }

    output.priority = priority;
    output.bs = derivedBs;
    output.bs_hash = MachineHelper.getBsHash(derivedBs, priority);
    output.traj = derivedTraj;

    output.seams = inputs.seams;
    output.breaks = inputs.breaks;
    output.plate_loc_backup = inputs.plate;

    // creating a pitch from a specific PL => the default list for save as should be the same list
    if (listId) {
      output._parent_id = listId;
    }

    return output;
  }, [refPitch, inputs, derivedBs, priority, derivedTraj, listId]);

  const formErrors = useMemo(
    () =>
      PitchDesignHelper.getFormErrors(
        {
          speedMph: inputs.speedMph,
          spins: inputs.spins,
          spinExt: inputs.spinExt,
          breaks: inputs.breaks,
        },
        priority,
        machine.isMetric
      ),
    [
      inputs.speedMph,
      inputs.spins,
      inputs.spinExt,
      inputs.breaks,
      priority,
      machine.isMetric,
    ]
  );

  const onUpdate = async () => {
    if (formErrors.length > 0) {
      NotifyHelper.warning({
        message_md: formErrors.map((err) => `- ${err}`).join('\n'),
      });
      return;
    }

    if (!refPitch._id) {
      console.warn('no reference pitch _id');
      return;
    }

    if (!refPitch._parent_id) {
      console.warn('no reference pitch _parent_id');
      return;
    }

    try {
      setLoading(true);

      // todo: investigate which of these behaviours is what we actually want in prod
      // add a new pitch that looks like the existing one
      if (ENABLE_REPEATED_UPDATES) {
        const newPitch =
          await PitchesService.getInstance().createOne(pitchPayload);

        if (!newPitch) {
          NotifyHelper.warning({
            message_md: t('pd.error-building-pitch-for-updating'),
          });
          return;
        }

        NotifyHelper.success({
          message_md: t('common.x-updated', {
            x: newPitch.name ?? t('common.pitch'),
          }),
        });

        // mark the old pitch as deleted
        await PitchesService.getInstance().markAsDeleted([refPitch._id]);
      } else {
        // todo: deprecate this block once we update prod server
        const addResult = await PitchListsService.getInstance().addPitches({
          listID: refPitch._parent_id,
          data: [pitchPayload],
        });

        if (!addResult.success) {
          NotifyHelper.warning({
            message_md: t('pd.error-building-pitch-for-updating'),
          });
          return;
        }

        const updatedPitch = first(addResult.data) as IPitch;

        NotifyHelper.success({
          message_md: t('common.x-updated', {
            x: pitchPayload.name ?? t('common.pitch'),
          }),
        });

        // mark the old pitch as deleted
        await usePitchListStore.getState().deletePitches([refPitch._id], true);

        await usePitchListStore
          .getState()
          .reloadPitches({ activeList: activeList });

        props.onPitchUpdated?.(updatedPitch);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onAddToPitchList = useCallback(async () => {
    if (formErrors.length > 0) {
      NotifyHelper.warning({
        message_md: formErrors.map((err) => `- ${err}`).join('\n'),
      });
      return;
    }

    setDialogSave({
      key: Date.now(),
      pitch: pitchPayload,
    });
  }, [pitchPayload]);

  const inExistingList = !!refPitch._parent_id;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION}>
        {!props.asDialog && (
          <SectionHeader
            header={t(isNew ? 'common.create-pitch' : 'common.update-pitch')}
            badge1={<Badge color={RADIX.COLOR.NEUTRAL}>{refPitch.name}</Badge>}
            actions={[
              {
                label: 'pd.add-to-pitch-list',
                prefixIcon: <PlusIcon />,
                invisible: !isNew,
                onClick: onAddToPitchList,
              },
              {
                label: 'common.update-pitch',
                prefixIcon: <FileIcon />,
                invisible: isNew,
                disabled: loading,
                onClick: onUpdate,
              },
            ]}
            breadcrumbs={
              fromListId
                ? [
                    {
                      label: 'common.pitches',
                      onClick: () => navigate(paths.pitches.library),
                    },
                    // breadcrumb for the list the user came from
                    {
                      label: 'common.pitch-list',
                      onClick: () => navigate(paths.pitches.list(fromListId)),
                    },
                    {
                      label: 'common.update-pitch',
                    },
                  ]
                : [
                    {
                      label: 'common.pitches',
                      onClick: () => navigate(paths.pitches.library),
                    },
                    {
                      label: 'common.create-pitch',
                    },
                  ]
            }
          />
        )}

        <CommonContentWithSidebar
          left={
            <Flex
              direction="column"
              gap={RADIX.FLEX.GAP.MD}
              height="100%"
              minHeight="700px"
            >
              <Card size="2">
                <InputModeForm />
              </Card>

              <Card size="2">
                <SeamOrientation />
              </Card>

              <BallFlightDesigner
                traj={derivedTraj}
                onUpdatePlate={(plate) =>
                  usePitchDesignStore.getState().mergeInputs({ plate: plate })
                }
                onUpdateRelease={(releasePos) =>
                  usePitchDesignStore.getState().mergeInputs({
                    releasePos: {
                      px: releasePos.px,
                      py: inputs.releasePos.py, // py never changes...
                      pz: releasePos.pz,
                    },
                  })
                }
                dialog={props.asDialog}
              />
            </Flex>
          }
          right={<PDSidebar loading={!derivedTraj} />}
          gap={RADIX.FLEX.GAP.MD}
        />
        {props.asDialog && (
          <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
            <DialogButton
              label="common.save-duplicate"
              variant="soft"
              // don't show this for unsaved pitches
              className={inExistingList ? '' : 'hidden'}
              onClick={onAddToPitchList}
            />
            <DialogButton
              label="common.save"
              variant="solid"
              onClick={inExistingList ? onUpdate : onAddToPitchList}
            />
          </Flex>
        )}
      </Flex>

      {dialogSave && (
        <CopyPitchesDialog
          key={dialogSave.key}
          title={
            <Flex gap="2" align="center">
              {t('pd.add-to-pitch-list')}
              <Badge>
                {PitchDesignHelper.getDisplayBuildPriority(priority)}
              </Badge>
            </Flex>
          }
          identifier="PitchDesignSavePitchDialog"
          pitches={[dialogSave.pitch]}
          description={t('pd.msg-new-pitch-ready').toString()}
          onCreated={() => {
            setDialogSave(undefined);
          }}
          onClose={() => setDialogSave(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
